type Props = {
  /** Brand to apply to the preview. Null/undefined → neutral NHS-blue fallback. */
  brand?: {
    accent: string;
    logoText: string | null;
    tagline: string;
    name: string;
  } | null;
  /** Kit code shown bottom-right (e.g. "KIT-FBC-01"). */
  code: string;
  /** Tube label ("EDTA", "SST", etc.) shown as a coloured chip. */
  tube?: string;
  /** Top-right text on the box lid — short. */
  rhText?: string;
};

const TUBE_COLOURS: Record<string, string> = {
  EDTA: "#5B2D8A",      // purple top
  SST: "#C9A227",       // gold top
  FLUORIDE: "#B85C00",  // orange top (grey/orange in practice — orange reads better)
  HEPARIN: "#0F7A5F",   // green top
  DEFAULT: "#8C8C8C",   // unmarked
};

/**
 * Lightweight top-down kit-box preview used on catalogue cards + the
 * brand-library cards. Pure SVG — no three.js, so we can render six on a
 * page without thrashing the GPU. The 3D box still lives on the customise
 * + approval pages where the live preview matters more.
 *
 * Layout mirrors the BoxMockup3D top face: brand logo (or initials)
 * top-left, kit code top-right, tube chip bottom-left, accent stripe
 * along the bottom edge.
 */
export function KitHero({ brand, code, tube, rhText }: Props) {
  const accent = brand?.accent ?? "#005EB8";
  const tagline = brand?.tagline ?? "Your trusted health partner";
  const orgName = brand?.name ?? "Your practice";
  const initials =
    brand?.logoText ??
    orgName
      .split(/\s+/)
      .map((w) => w[0])
      .join("")
      .slice(0, 3)
      .toUpperCase();
  const tubeColour = tube ? (TUBE_COLOURS[tube] ?? TUBE_COLOURS.DEFAULT) : TUBE_COLOURS.DEFAULT;

  return (
    <svg
      viewBox="0 0 320 240"
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={`Kit preview · ${code}`}
      className="h-full w-full"
    >
      {/* Soft floor with a long shadow */}
      <defs>
        <linearGradient id={`kh-floor-${code}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#F6F8FA" />
          <stop offset="100%" stopColor="#E7ECEF" />
        </linearGradient>
        <linearGradient id={`kh-lid-${code}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FFFFFF" />
          <stop offset="100%" stopColor="#F2F4F5" />
        </linearGradient>
        <filter id={`kh-shadow-${code}`} x="-20%" y="-20%" width="140%" height="160%">
          <feGaussianBlur stdDeviation="6" />
        </filter>
      </defs>

      <rect x="0" y="0" width="320" height="240" fill={`url(#kh-floor-${code})`} />

      {/* Drop shadow under the box */}
      <ellipse
        cx="160"
        cy="200"
        rx="120"
        ry="14"
        fill="#1B2733"
        opacity="0.10"
        filter={`url(#kh-shadow-${code})`}
      />

      {/* Box lid — top-down, slight perspective */}
      <g transform="translate(40 60)">
        {/* Body */}
        <rect
          x="0"
          y="0"
          width="240"
          height="120"
          rx="3"
          fill={`url(#kh-lid-${code})`}
          stroke="#D8DDE0"
          strokeWidth="1"
        />

        {/* Accent stripe along the bottom */}
        <rect x="0" y="106" width="240" height="14" rx="0" fill={accent} />

        {/* Top-left: logo (initials block) + tagline */}
        <text
          x="12"
          y="32"
          fontFamily="Inter, system-ui, sans-serif"
          fontWeight="800"
          fontSize="22"
          letterSpacing="-0.5"
          fill={accent}
        >
          {initials}
        </text>
        <text
          x="12"
          y="46"
          fontFamily="Inter, system-ui, sans-serif"
          fontWeight="600"
          fontSize="7"
          fill="#212B32"
        >
          <TextClamp text={tagline} max={32} />
        </text>

        {/* Top-right: right-hand text + kit name */}
        <g textAnchor="end">
          <text
            x="228"
            y="28"
            fontFamily="Inter, system-ui, sans-serif"
            fontWeight="700"
            fontSize="9"
            fill="#1B2733"
            letterSpacing="-0.1"
          >
            <TextClamp text={rhText ?? "Self-test kit"} max={28} />
          </text>
          <text
            x="228"
            y="40"
            fontFamily="Inter, system-ui, sans-serif"
            fontWeight="600"
            fontSize="6"
            fill="#565458"
            letterSpacing="0.6"
          >
            SELF-TESTING KIT
          </text>
        </g>

        {/* Bottom-left: tube chip */}
        <g transform="translate(12 78)">
          <rect width="36" height="14" rx="2" fill={tubeColour} opacity="0.92" />
          <text
            x="18"
            y="10"
            textAnchor="middle"
            fontFamily="JetBrains Mono, monospace"
            fontWeight="700"
            fontSize="7"
            fill="white"
            letterSpacing="0.5"
          >
            {tube ?? ""}
          </text>
        </g>

        {/* Bottom-right: kit code */}
        <text
          x="228"
          y="88"
          textAnchor="end"
          fontFamily="JetBrains Mono, monospace"
          fontSize="8"
          fill="#565458"
          letterSpacing="0.4"
        >
          {code}
        </text>
      </g>
    </svg>
  );
}

/** Trim a string to roughly fit by character count. SVG `<text>` has no
 *  wrapping or clipping; manual trim avoids overflow into the next field. */
function TextClamp({ text, max }: { text: string; max: number }) {
  return <>{text.length > max ? text.slice(0, max - 1).trimEnd() + "…" : text}</>;
}
